import {
  codeSplit,
  Controller,
  FetchOptions,
  FetchPageOptions,
  AnalyticsStore,
  PageStore,
  Result,
  StoreContainer,
} from "bernie-core";
import { PageData, HttpStatus, Request } from "bernie-http";
import {
  Composition,
  ExtendedContextStore,
  FlexBffResponse,
  FlexBffTemplateResponse,
  PageType,
  Template,
  ViewModelResponse,
} from "typings/flexFramework/FlexDefinitions";
import { TemplateStore } from "stores/TemplateStore";
import { CompositionStore } from "src/stores/CompositionStore";
import { FlexViewModelStore } from "stores/flexViewModel/FlexViewModelStore";
import { StaticMapStore } from "stores/staticMap/StaticMapStore";
import { blossomCodeSplitter } from "src/components/flexFramework/CodeSplitter/BlossomCodeSplitter";
import { FlexModuleModelStore } from "src/stores/FlexModuleModelStore";
import { FlexSeoWithCompositionResponse } from "utils/seo/domain/FlexSeoWithCompositionResponse";
import { CookiesStore } from "stores/CookiesStore";
import { ServerWizardStateStore } from "stores/wizard/ServerWizardStateStore";
import { DXStore } from "stores/DXStore";
import { ServerChatbotStore } from "stores/chatbotStore/ServerChatbotStore";
import { MabStore } from "stores/mabStore/MabStore";
import { PropertyFiltersStore } from "stores/PropertyFiltersStore";
import { Experiment } from "components/utility/experiment";
import { RateCalendarStore } from "src/stores/rateCalendar";
import { UriContextStore } from "src/stores/UriContextStore/UriContextStore";

export interface BffResponse extends FlexBffResponse {
  composition?: Composition;
  template?: Template;
}

interface FlexFetchPageOptions extends FetchPageOptions {
  context: ExtendedContextStore;
}

interface WindowWithState extends Window {
  __PLUGIN_STATE__: {
    mobx: {
      stores: {
        templateStore: TemplateStore;
        compositionStore: CompositionStore;
        context: ExtendedContextStore;
      };
    };
  };
}

declare let window: WindowWithState;

/**
 * Supports both composition (old) vs template rendering paths
 * Currently work in progress in order to work towards the BEXG Tech Strategy.
 * More info here: More info: https://confluence.expedia.biz/display/~mhaddock/Flex%3A+full+adoption+of+Tech+Strategy
 */
export class FlexController implements Controller {
  public pageId: string; // x-page-id is set in the middleware

  public path = "/*";

  public routeName = "flex-FlexPage";

  public bundles = ["flex-FlexPage"];

  public exact = false;

  public component = codeSplit(async () => {
    const routeComponent = import(/* webpackChunkName: "blossom-FlexPage" */ "views/FlexPage") as any;
    // Either template or composition is populated
    let template;
    let composition;

    // Client-side only
    if (typeof window !== "undefined") {
      const serializedStores = window.__PLUGIN_STATE__.mobx.stores;
      template = serializedStores.templateStore?.template;
      composition = serializedStores.compositionStore?.composition;
    }

    await blossomCodeSplitter.loadRequiredCodeSplitBundles({ template, composition });

    return routeComponent;
  });

  public async fetch(options: FetchOptions): Promise<Result> {
    const { stores } = options;
    const flexViewModelStore = stores.get<FlexViewModelStore>("flexViewModel");
    const pageStore = stores.get<PageStore>("page");
    const contextStore = stores.get<ExtendedContextStore>("context");
    const mapStore = stores.get<StaticMapStore>("staticMap");
    const compositionStore = stores.get<CompositionStore>("compositionStore");
    const templateStore = stores.get<TemplateStore>("templateStore");
    const flexModuleModelStore = stores.get<FlexModuleModelStore>("flexModuleModelStore");
    const mabStore = stores.get<MabStore>("mab");
    const analyticsStore = stores.get<AnalyticsStore>("analytics");
    const uriContextStore = stores.get<UriContextStore>("uriContext");

    // We get it from the middleware which hijacks the contextStore
    const flexBffResponse = contextStore.viewModelResponse as FlexBffTemplateResponse | undefined;

    delete contextStore.viewModelResponse;

    // Core logic
    flexViewModelStore.setupFlexViewModelStore(flexBffResponse, pageStore, contextStore, analyticsStore);
    templateStore.setup(flexBffResponse?.template);
    compositionStore.setup(flexBffResponse, contextStore, flexModuleModelStore, mapStore);
    mabStore.setVariantsAndLog(flexBffResponse?.flexContext, analyticsStore);
    uriContextStore.setup(flexBffResponse);

    // Extra stores to prepare
    await this.prepareAdditionalStores(options, flexBffResponse);

    return this.fetchDeferredData(options);
  }

  /**
   * A promise to fetch Flex Composition data. Will be executed in parallel with GraphQL queries by bernie-server
   */
  public fetchDeferredData(options: FetchOptions): Promise<any> {
    const templateStore = options.stores.get<TemplateStore>("templateStore");
    const compositionStore = options.stores.get<CompositionStore>("compositionStore");
    const contextStore = options.stores.get<ExtendedContextStore>("context");
    const analyticsStore = options.stores.get<AnalyticsStore>("analytics");
    const pageStore = options.stores.get<PageStore>("page");
    const mapStore = options.stores.get<StaticMapStore>("staticMap");
    const rateCalendarStore = options.stores.get<RateCalendarStore>("rateCalendar");
    const flexViewmodelStore = options.stores.get<FlexViewModelStore>("flexViewModel");

    // If composition populated, then we are in the "old" path
    if (compositionStore?.composition) {
      // This will not work in the template rendering path, because we don't have the composition
      // meaning we don't know the hotel ids yet.
      const rateCalendarPromise = rateCalendarStore.setup(
        flexViewmodelStore.flexContext,
        contextStore,
        compositionStore
      );

      return Promise.resolve({
        status: HttpStatus.OK,
        next: () => rateCalendarPromise,
      });
    }

    // Otherwise, we need to fetch composition in parallel to bex-api calls
    const compositionPromise = compositionStore
      .fetchCompositionFromTemplate(
        templateStore.template,
        options.request.headers,
        contextStore,
        analyticsStore,
        pageStore,
        mapStore
      )
      .then((data) => new FlexSeoWithCompositionResponse(data).overridePageStore(pageStore));

    return Promise.resolve({
      status: HttpStatus.OK,
      next: () => compositionPromise,
    });
  }

  public fetchPageData(options: FlexFetchPageOptions): Promise<PageData> {
    const bffResponse = options.context.viewModelResponse as BffResponse;

    // Either template or composition is populated
    const additionalScripts = blossomCodeSplitter
      .getRequiredWebpackChunkNames({
        template: bffResponse.template,
        composition: bffResponse.composition,
      })
      .filter((m) => !!m);

    const seoMetaData = import(/* webpackChunkName: "SeoUtils" */ "utils/seo/SeoUtils").then(({ default: _ }) => {
      if (bffResponse.composition) {
        return (
          _.buildPageDataWithSeoMetaDataFromViewModel(bffResponse as ViewModelResponse) || {
            title: bffResponse.composition.title,
          }
        );
      }

      return (
        _.buildPageDataWithSeoMetaDataFromFlexBffTemplate(bffResponse as FlexBffTemplateResponse) || {
          title: bffResponse.template?.title || "",
        }
      );
    });

    return Promise.resolve(seoMetaData).then((m) => {
      return {
        ...m,
        assetsToIncludeFilter: (assetKey: string) => additionalScripts.includes(assetKey),
        pageId: options.context.pageId,
      };
    });
  }

  // Non-essential stores to prepare
  private async prepareAdditionalStores(options: FetchOptions, flexBffResponse?: BffResponse) {
    const { stores, request } = options;
    const chatbotStore = stores.get<ServerChatbotStore>("chatbot");
    const dxStore = stores.get<DXStore>("dx");
    const propertyFiltersStore = stores.get<PropertyFiltersStore>("propertyFilters");
    const wizardState = stores.get<ServerWizardStateStore>("wizardState");
    const cookiesStore = stores.get<CookiesStore>("cookies");
    const contextStore = stores.get<ExtendedContextStore>("context");

    //  Get cookie string for wizard
    cookiesStore.cookie = request.headers.cookie || "";
    if (wizardState && wizardState.setLastSearchState) {
      wizardState.setLastSearchState(cookiesStore.getCookie("lastSearchCookie"), contextStore);
    }

    if (typeof chatbotStore.initChatbotStore === "function") {
      await chatbotStore.initChatbotStore(request, contextStore);
    }

    if (propertyFiltersStore && propertyFiltersStore.setRegion) {
      propertyFiltersStore.setRegion(contextStore?.searchContext?.location?.id || "");
    }

    dxStore.setupInitialJumplinks(flexBffResponse);
  }

  private pageTypesToStream: PageType[] = [
    "Hotel-Information",
    "Travel-Guide-Hotels",
    "Destination-Travel-Guides",
    "Travel-Guide-Accommodation",
    "Travel-Guide-Filter-Hotels",
    "Travel-Guide-VacationRentals",
    "Alternative-Accommodations",
    "Hotel-Destinations",
    "Hotel-Filter-Destinations",
    "Vacation-Rental-Destinations",
    "Travel-Guide-Airlines",
    "Airline-Ond-Airport",
    "Travel-Guide-Flights",
    "Flight-Origin-City",
    "Flight-Destinations",
    "Flight-Ond-City",
    "Flight-Ond-Airport",
    "Car-Rental-Guide",
    "Car-Rental-Destinations",
    "Travel-Guide-CarRentalCompany",
    "Car-Rental-Guide-Car-Class",
    "Car-Rental-Guide-Suppliers",
    "Car-Rental-Destination-Suppliers",
    "Car-Rental-Supplier-Locations",
  ];

  /**
   * Happens before `fetch` is called, so the viewModelResponse is still in the context
   */
  public shouldStreamResponse(request: Request, stores: StoreContainer): boolean {
    const contextStore = stores.get<ExtendedContextStore>("context");

    if (!contextStore) {
      return false;
    }

    const forceStreaming = contextStore.queryParams?.["streaming"] === "1";
    const isTemplateRendering = contextStore.queryParams?.["withTemplate"] === "1";

    const pageType = contextStore.viewModelResponse?.flexContext?.searchContext?.pageType;
    const experiments = contextStore.viewModelResponse?.flexContext?.experimentContext?.experiments;

    const isStreamingTnL = experiments?.[Experiment.Blossom_Node_Streaming] === 1;

    // Always stream given the query parameter
    if (forceStreaming) {
      return true;
    }

    // Do not use streaming on the template rendering path, because tokens will be missing
    // in the title and meta tags.
    if (isTemplateRendering) {
      return false;
    }

    // Stream some pageTypes 100% and others based on the experiment.
    return (pageType && this.pageTypesToStream.includes(pageType)) || isStreamingTnL;
  }
}
